import DebugObject from '@/etc/debug.object'
import * as TWEEN from '@tweenjs/tween.js'
import { EASING_FN } from '@/etc/consts'
import { jclone } from '@/etc/utils'

export default class Animation extends DebugObject {
  constructor(animation, initialValues) {
    super();
    this._animation = animation;
    this.dbg.log('init', animation, initialValues);
    this._time = 0;
    // Clone initial values so runtime evaluation never mutates Vuex state objects
    this._values = jclone(initialValues || {})
    this._tweenValues = {};
    this._kfValues = jclone(initialValues || {})
    this._tweens = {};
    this._tweenGroup = new TWEEN.Group();
    this.buildTweens();
    this.dbg.log('constructor')
  }

  buildTweens(animation = null) {
    if (animation) this._animation = animation
    const values = jclone(this._values)
    this._tweenGroup.removeAll()
    this._tweens = {};
    const keyframes = jclone(this._animation.keyframes).sort(
      (a, b) => a.time - b.time
    )
    const kfEntry = keyframes.find(kf => kf.type === 'entry')
    if (kfEntry && kfEntry.props) {
      Object.keys(kfEntry.props).forEach(prop => {
        values[prop] = kfEntry.props[prop]
      })
    }
    this.dbg.log('buildTweens, keyframes:', keyframes)
    const timeline = []
    keyframes.forEach(kf => {
      Object.keys(kf.props).forEach(prop => {
        timeline.push({
          time: kf.time,
          values: { [prop]: kf.props[prop] },
          tween: {
            [prop]: kf.delay[prop] === 0 ? kf.easing[prop] : false
          }
        })
        if (kf.delay[prop])
          timeline.push({
            time: kf.time + kf.delay[prop],
            values: { [prop]: kf.props[prop] },
            tween: { [prop]: kf.easing[prop] }
          })
      })
    })
    this.dbg.log('buildTweens, timeline:', timeline)
    const bytime = timeline.reduce((acc, tm) => {
      if (!acc[tm.time])
        acc[tm.time] = {
          time: tm.time,
          values: {},
          tween: {},
        }
      acc[tm.time].values = {
        ...acc[tm.time].values,
        ...tm.values
      }
      acc[tm.time].tween = {
        ...acc[tm.time].tween,
        ...tm.tween
      }
      return acc
    }, {})

    const index = Object.values(bytime).sort((a, b) => a.time - b.time)
    let last = this._values
    this.dbg.log('index?', index)
    this.dbg.log('_values?', this._values)
    if (kfEntry && kfEntry.props) {
      Object.keys(kfEntry.props).forEach(prop => {
        last[prop] = kfEntry.props[prop]
      })
    }
    this.dbg.log('last?', last)
    const tweens = {}
    const frames = index.map((frame) => {
      const ret = {
        time: frame.time,
        values: {
          ...last,
          ...frame.values
        },
        tween: {}
      }
      Object.keys(frame.tween).forEach(prop => {
        if (frame.tween[prop]) {
          const end = index.find(tf => tf.time > frame.time /* && tf.tween[prop] === false*/) || index[index.length - 1]
          // Tween.js turns non-numeric values (font names, colors) into null; hold them instead.
          const endValue = end.values[prop]
          if (typeof frame.values[prop] !== 'number' || (typeof endValue !== 'undefined' && typeof endValue !== 'number')) return
          const easingFn = EASING_FN[frame.tween[prop]] || EASING_FN['Linear.None']
          // this.dbg.info('frame tween prop', prop, frame.tween[prop], easingFn)
          this.dbg.log('TWEEN', prop, frame, end)
          const tween = new TWEEN.Tween({ value: frame.values[prop] }, this._tweenGroup)
            .to({ value: end.values[prop] }, Math.floor((end.time - frame.time) * 1000))
            .easing(easingFn)
            .start(0)
          // if (EASING_FN[])
          if (!tweens[prop])
            tweens[prop] = []
          ret.tween[prop] = tweens[prop].length
          tweens[prop].push({
            start: frame.time,
            end: end.time,
            tween
          })
        }
      })
      last = { ...ret.values }
      return ret
    })
    this._tweens = {
      frames: frames.map(frame => {
        const next = { ...frame }
        Object.keys(frame.values).forEach(prop => {
          const twi = (tweens[prop] || []).findIndex(tw => tw.start < frame.time && tw.end > frame.time)
          if (twi >= 0)
            next.tween[prop] = twi
        })
        return next
      }),
      tweens
    }
    this.dbg.debug('tweens', this._tweens)

    // Phase 1: Build Legacy Keyframes V2 per-property index
    try {
      const perProp = {}
      const kfs = jclone(this._animation?.keyframes || []).sort((a, b) => a.time - b.time)
      kfs.forEach(kf => {
        const props = kf.props || {}
        const delays = kf.delay || {}
        const easings = kf.easing || {}
        const modes = kf.interpOut || {}
        Object.keys(props).forEach(code => {
          if (!perProp[code]) perProp[code] = []
          const delay = Number.isFinite(delays[code]) ? Math.max(0, delays[code]) : 0
          const easing = easings[code] || 'Linear.None'
          const mode = (modes && typeof modes[code] === 'string') ? modes[code] : 'tween'
          perProp[code].push({ time: kf.time, value: props[code], delay, easing, mode })
        })
      })
      Object.keys(perProp).forEach(code => perProp[code].sort((a, b) => a.time - b.time))
      this._legacyIndex = perProp
    } catch (e) {
      this._legacyIndex = {}
    }
  }

  setTime(next) {
    this._time = next;
  }

  propsAt(time) {
    // Phase 1: Legacy Keyframes V2 evaluator guarded by feature flag
    try {
      const store = require('@/store').default
      const v2 = !!store.state.project.settings.legacyKeyframesV2
      if (v2) {
        const out = {}
        const defaults = this._values || {}
        const perProp = this._legacyIndex || {}

        const getEase = (key) => {
          try { return EASING_FN[key] || EASING_FN['Linear.None'] } catch (e) { return (x) => x }
        }

        const propSet = new Set([ ...Object.keys(defaults || {}), ...Object.keys(perProp || {}) ])
        propSet.forEach(code => {
          const track = perProp[code] || []
          if (!track.length) {
            out[code] = defaults[code]
            return
          }
          if (time <= track[0].time) {
            out[code] = track[0].value
            return
          }
          let i = 0
          for (; i < track.length - 1; i++) {
            if (time < track[i + 1].time) break
          }
          if (i >= track.length - 1) {
            out[code] = track[track.length - 1].value
            return
          }
          const curr = track[i]
          const next = track[i + 1]
          const mode = (curr && typeof curr.mode === 'string') ? curr.mode : 'tween'
          const segEnd = next.time
          if (mode === 'hold') {
            out[code] = curr.value
            return
          }
          const holdEnd = curr.time + (Number.isFinite(curr.delay) ? Math.max(0, curr.delay) : 0)
          if (time <= Math.min(segEnd, holdEnd) || holdEnd >= segEnd) {
            out[code] = curr.value
            return
          }
          const span = Math.max(1e-6, segEnd - holdEnd)
          const u = Math.min(1, Math.max(0, (time - holdEnd) / span))
          const easeFn = getEase(curr.easing)
          const m = typeof easeFn === 'function' ? easeFn(u) : u
          const a = Number(curr.value)
          const b = Number(next.value)
          if (Number.isFinite(a) && Number.isFinite(b)) out[code] = a + (b - a) * m
          else out[code] = u < 1 ? curr.value : next.value
        })
        return out
      }
    } catch (e) { /* ignore flag read errors */ }

    const tf = this._tweens.frames.findLast(fr => fr.time <= time)
    this.dbg.log('propsAt', time, tf)
    if (!tf) {
      this.dbg.warn('propsAt - timeframe not found at index', time, this._tweens)
      return this._tweenValues;
    }
    const ret = { ...tf.values }
    Object.keys(tf.tween).forEach(prop => {
      const tw = this._tweens.tweens[prop][tf.tween[prop]]
      const lc = time - tw.start
      this.dbg.log('propsAt2', prop, lc, ret[prop], tw)
      // Phase 9: guard against negative durations/NaNs
      const ms = Math.max(0, Math.floor(lc * 1000))
      tw.tween.update(ms)
      this.dbg.log('propsAt3', prop, lc, ret[prop], tw.tween._object.value)
      ret[prop] = tw.tween._object.value
    })
    return ret
  }
}
